import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { useApiMutation } from '../useApiMutation';
import { useApiQuery } from '../useApiQuery';
import {
  apiGet,
  apiPost,
  formsEngineEndpoints,
  isApiError,
  normalizeError,
  operationsClient,
} from '@/lib/api';
import { formsEngineKeys } from '@/lib/queryKeys';
import type {
  IntakeActivityResponse,
  IntakeActivitySummaryResponse,
  IntakeSubmissionListPage,
} from '@/types/formsEngine';

/**
 * The builder's Activity tab (Task 11) — the NEEDS_ATTENTION submissions of
 * one intake template, plus the counts (spam and `leads30d` included) the tab
 * strip and the library badge both read. Spam rows are not asked for here:
 * the pinned spam section pages through its own list (`useFormSubmissions`
 * with status SPAM). A healthy form comes back with empty `items` and
 * all-zero `counts`.
 */
export function useFormActivity(templateId: string | undefined) {
  return useApiQuery<IntakeActivityResponse>({
    queryKey: formsEngineKeys.intakeActivity(templateId ?? ''),
    queryFn: () =>
      apiGet<IntakeActivityResponse>(operationsClient, formsEngineEndpoints.intakeActivity(), {
        params: { templateId: templateId as string, status: 'NEEDS_ATTENTION' },
      }),
    enabled: Boolean(templateId),
    showErrorToast: false,
  });
}

/**
 * Every submission of one intake template but spam, newest first — the
 * Activity tab's full list under the pinned exceptions. With
 * `status: 'SPAM'`, only its spam, for the pinned spam section. Pages by the
 * server's `nextCursor`.
 */
export function useFormSubmissions(
  templateId: string | undefined,
  { status }: { status?: 'SPAM' } = {},
) {
  return useInfiniteQuery({
    queryKey: formsEngineKeys.intakeSubmissions(templateId ?? '', status),
    enabled: Boolean(templateId),
    initialPageParam: null as string | null,
    queryFn: async ({ pageParam }) => {
      try {
        return await apiGet<IntakeSubmissionListPage>(
          operationsClient,
          formsEngineEndpoints.intakeSubmissions(),
          {
            params: {
              templateId: templateId as string,
              ...(status ? { status } : {}),
              ...(pageParam ? { cursor: pageParam } : {}),
            },
          },
        );
      } catch (err) {
        throw isApiError(err) ? err : normalizeError(err);
      }
    },
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    meta: { suppressToast: true },
  });
}

/**
 * One row per intake template with any activity, for the Forms library's
 * "N leads (30 days)" / "N need attention" badges. Callers gate `enabled` on
 * whether the library actually has an INTAKE row — there's nothing to badge
 * otherwise, so the request shouldn't fire.
 */
export function useIntakeActivitySummary(options: { enabled?: boolean } = {}) {
  return useApiQuery<IntakeActivitySummaryResponse>({
    queryKey: formsEngineKeys.intakeActivitySummary(),
    queryFn: () =>
      apiGet<IntakeActivitySummaryResponse>(
        operationsClient,
        formsEngineEndpoints.intakeActivitySummary(),
      ),
    enabled: options.enabled ?? true,
    showErrorToast: false,
  });
}

export interface ActivityActionVariables {
  id: string;
  /** Which template's activity + the library summary to refresh on success. */
  templateId: string;
}

function invalidateActivity(
  queryClient: ReturnType<typeof useQueryClient>,
  templateId: string,
) {
  void queryClient.invalidateQueries({ queryKey: formsEngineKeys.intakeActivity(templateId) });
  void queryClient.invalidateQueries({ queryKey: formsEngineKeys.intakeSubmissions(templateId) });
  void queryClient.invalidateQueries({ queryKey: formsEngineKeys.intakeActivitySummary() });
}

/**
 * Shared shape for the three row actions below. Errors (including the 409 a
 * row-in-flight mutation or another staff member racing us produces) are left
 * to propagate — `showErrorToast: false` so the caller, which knows how to
 * tell "already handled" apart from a genuine failure, decides how to show it
 * rather than a generic toast beating it there.
 */
function useActivityAction(endpoint: (id: string) => string) {
  const queryClient = useQueryClient();
  return useApiMutation<unknown, ActivityActionVariables>({
    mutationFn: ({ id }) => apiPost(operationsClient, endpoint(id)),
    showErrorToast: false,
    onSuccess: (_result, { templateId }) => invalidateActivity(queryClient, templateId),
  });
}

/** NEEDS_ATTENTION → PENDING, so the worker picks it up again. */
export function useRetrySubmission() {
  return useActivityAction(formsEngineEndpoints.intakeSubmissionRetry);
}

/** NEEDS_ATTENTION → DISMISSED. Permanent — no further retries. */
export function useDismissSubmission() {
  return useActivityAction(formsEngineEndpoints.intakeSubmissionDismiss);
}

/** SPAM → PENDING, so the worker re-evaluates it as a normal submission. */
export function useMarkNotSpam() {
  return useActivityAction(formsEngineEndpoints.intakeSubmissionMarkNotSpam);
}
